package com.banyan.flightviewer.web.components.airport

import com.banyan.flightviewer.data.AirlineDatabase
import com.banyan.flightviewer.data.AirportDatabase
import com.banyan.flightviewer.state.AirportFlight
import com.banyan.flightviewer.web.components.util.formatTime12Hour
import com.banyan.flightviewer.web.dom.div
import com.banyan.flightviewer.web.dom.span
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.w3c.dom.Element

/**
 * Renders a list of AirportFlight rows (used by both the Departures and
 * Arrivals tabs). Mirrors the desktop FlightRow composable:
 *   - Time+status column (72px fixed)
 *   - Airline column (90px fixed, 2 lines max)
 *   - Destination/origin + flight number + aircraft-type badge (flex)
 *
 * [loader] is a suspending function that returns the flight list to
 * display (usually from [AirportCache] or a server fetch). Called once
 * per mount; lists are small enough to render all at once (no
 * virtualisation needed).
 *
 * [isDeparture] flips origin vs destination display. True = show where
 * the flight is going (destination + name); false = show where it's
 * coming from (origin + name).
 */
fun mountFlightListTab(
    parent: Element,
    scope: CoroutineScope,
    isDeparture: Boolean,
    loader: suspend () -> List<AirportFlight>,
) {
    val container = parent.div(className = "fv-airport__flightlist")
    val loading = container.div(className = "fv-airport__loading") {
        textContent = "Loading\u2026"
    }

    scope.launch {
        val flights = loader()
        container.removeChild(loading)
        if (flights.isEmpty()) {
            container.div(className = "fv-airport__empty") {
                textContent = if (isDeparture) "No departures found" else "No arrivals found"
            }
            return@launch
        }
        flights.forEach { flight ->
            renderFlightRow(container, flight, isDeparture)
        }
    }
}

private fun renderFlightRow(parent: Element, flight: AirportFlight, isDeparture: Boolean) {
    val airlineName = AirlineDatabase.getAirlineName(flight.callsign)
        ?: flight.airline.takeIf { it.isNotBlank() }
    val targetAirport = if (isDeparture) flight.destination else flight.origin
    val targetName = AirportDatabase.getName(targetAirport).takeIf { it != targetAirport } ?: ""
    val time = formatTime12Hour(flight.actualTime.ifBlank { flight.scheduledTime })
        .ifBlank { "\u2014" }
    val callsign = flight.flightNumber.ifBlank { flight.callsign }
    val statusText = when {
        flight.status.isBlank() -> ""
        flight.status.equals("en route", true) -> "En route"
        else -> flight.status.replaceFirstChar { it.uppercase() }
    }
    val statusClass = if (flight.status.equals("en route", true)) "enroute" else ""

    parent.div(className = "fv-airport__flight-row") {
        // Column 1: time + status
        div(className = "fv-airport__flight-time") {
            div(className = "fv-airport__flight-time-big") { textContent = time }
            if (statusText.isNotBlank()) {
                div(className = "fv-airport__flight-status $statusClass".trim()) {
                    textContent = statusText
                }
            }
        }

        // Column 2: airline (fixed width, truncated to 2 lines via CSS)
        div(className = "fv-airport__flight-airline") {
            textContent = airlineName ?: ""
        }

        // Column 3: destination/origin details
        div(className = "fv-airport__flight-dest") {
            // Line 1: airport name + code badge
            div(className = "fv-airport__flight-dest-line") {
                if (targetName.isNotBlank()) {
                    span(className = "fv-airport__flight-destname") {
                        textContent = targetName
                    }
                }
                if (targetAirport.isNotBlank()) {
                    span(className = "fv-airport__code-badge") {
                        textContent = targetAirport
                    }
                }
            }
            // Line 2: flight number + aircraft-type badge
            div(className = "fv-airport__flight-dest-line") {
                span(className = "fv-airport__flight-num") { textContent = callsign }
                if (flight.aircraftType.isNotBlank() && flight.aircraftType != "Unknown") {
                    span(className = "fv-airport__type-badge") {
                        textContent = flight.aircraftType
                    }
                }
            }
        }
    }
}
